/**
 * Persona Studio Playground page
 * Test and iterate on personas with live content generation
 */

import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router';

import { Button, Card, Input, Select, Icon, Textarea, toast } from '@lib/ui';
import { useAuthContext } from '@lib/auth';
import { PersonaPreview, DecisionSelector } from '../../poc/wizards/components';
import { usePersonas, useUpdatePersona } from '../hooks/usePersonaApi';
import type { Persona, PersonaDecisions, WizardPersona } from '../types';
import {
  SAMPLE_PROMPTS,
  CHANNELS,
  DECISION_DIMENSIONS,
  defaultDecisions,
  personaToWizard,
} from '../types';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';

const LANGUAGE_OPTIONS = [
  { value: 'english', label: 'English' },
  { value: 'hebrew', label: 'Hebrew' },
];

interface PersonaImprovementSuggestion {
  field: string;
  current: string;
  suggested: string;
  reason: string;
}

interface ImprovePersonaResponse {
  suggestions: PersonaImprovementSuggestion[];
  summary: string;
}

export default function PersonaStudioPlayground() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedPersonaId = searchParams.get('persona');
  const { getAccessToken } = useAuthContext();

  // API hooks
  const { personas, isLoading: isLoadingPersonas, refetch } = usePersonas();
  const { updatePersona, isUpdating } = useUpdatePersona();

  // State
  const [selectedPersonaId, setSelectedPersonaId] = useState<string>('');
  const [selectedChannel, setSelectedChannel] = useState<string>('LinkedIn');
  const [selectedLanguage, setSelectedLanguage] = useState<string>('english');
  const [customTopic, setCustomTopic] = useState<string>('');
  const [selectedTopic, setSelectedTopic] = useState<string>(SAMPLE_PROMPTS[0]);

  // Working copy of persona for live adjustments
  const [workingPersona, setWorkingPersona] = useState<Partial<WizardPersona> | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // AI Feedback state
  const [feedbackInput, setFeedbackInput] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [suggestions, setSuggestions] = useState<ImprovePersonaResponse | null>(null);

  // Set preselected persona when data loads
  useEffect(() => {
    if (preselectedPersonaId && personas.length > 0) {
      const found = personas.find((p) => p.id === preselectedPersonaId);
      if (found) {
        setSelectedPersonaId(found.id);
        setWorkingPersona(personaToWizard(found));
      }
    }
  }, [preselectedPersonaId, personas]);

  // Handle persona selection
  const handlePersonaChange = (id: string) => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm('You have unsaved changes. Discard them?');
      if (!confirmed) return;
    }

    setSelectedPersonaId(id);
    setSuggestions(null);
    const persona = personas.find((p) => p.id === id);
    if (persona) {
      setWorkingPersona(personaToWizard(persona));
      setHasUnsavedChanges(false);
    } else {
      setWorkingPersona(null);
    }
  };

  // Handle decision changes
  const updateDecision = (key: keyof PersonaDecisions, value: string) => {
    if (!workingPersona) return;

    setWorkingPersona((prev) => ({
      ...prev,
      decisions: {
        ...(prev?.decisions ?? defaultDecisions),
        [key]: value,
      },
    }));
    setHasUnsavedChanges(true);
  };

  // Save changes to API
  const handleSave = async () => {
    if (!selectedPersonaId || !workingPersona) return;

    try {
      await updatePersona(selectedPersonaId, {
        name: workingPersona.name,
        tagline: workingPersona.tagline,
        worldview: workingPersona.worldview as Persona['worldview'],
        voice: workingPersona.voice as Persona['voice'],
        decisions: workingPersona.decisions as Persona['decisions'],
      });

      toast.success('Changes saved successfully');
      setHasUnsavedChanges(false);
      refetch();
    } catch {
      toast.error('Failed to save changes');
    }
  };

  // Reset to original
  const handleReset = () => {
    const persona = personas.find((p) => p.id === selectedPersonaId);
    if (persona) {
      setWorkingPersona(personaToWizard(persona));
      setHasUnsavedChanges(false);
      setSuggestions(null);
    }
  };

  // AI Feedback - analyze output and suggest persona changes
  const handleAnalyzeFeedback = async () => {
    if (!feedbackInput.trim() || !workingPersona) return;

    setIsAnalyzing(true);
    setSuggestions(null);

    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE}/poc/improve-persona`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_persona: JSON.stringify(workingPersona, null, 2),
          feedback: feedbackInput,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to analyze feedback');
      }

      const data = await response.json();

      // Parse the improved persona suggestions
      // The API returns improved_persona and changes_summary
      setSuggestions({
        suggestions: [],
        summary: data.changes_summary || 'AI has suggested improvements to your persona.',
      });

      // If we got an improved persona, parse it and apply as suggestions
      if (data.improved_persona) {
        // For now, show the summary and let user decide
        toast.success('AI has analyzed your feedback');
      }
    } catch {
      toast.error('Failed to analyze feedback');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Apply AI suggestion to persona
  const handleApplySuggestion = (suggestion: PersonaImprovementSuggestion) => {
    if (!workingPersona) return;

    // Apply the suggestion based on the field
    const field = suggestion.field.toLowerCase();

    if (field.includes('confidence')) {
      updateDecision('confidence', suggestion.suggested);
    } else if (field.includes('formality')) {
      updateDecision('formality', suggestion.suggested);
    } else if (field.includes('energy')) {
      updateDecision('energy', suggestion.suggested);
    } else if (field.includes('call') || field.includes('cta')) {
      updateDecision('callToAction', suggestion.suggested);
    }

    toast.success(`Applied: ${suggestion.field}`);
  };

  // Get effective topic
  const effectiveTopic = customTopic.trim() || selectedTopic;

  // Available channels for selector (including WhatsApp)
  const channelOptions = CHANNELS.slice(0, 5).map((ch) => ({
    value: ch,
    label: ch,
  }));

  // Persona selector options
  const personaOptions = [
    { value: '', label: 'Select a persona...' },
    ...personas.map((p) => ({ value: p.id, label: p.name })),
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Persona Playground
          </h1>
          <p className="mt-1 text-gray-500 dark:text-dark-300">
            Test and fine-tune your personas with live content generation
          </p>
        </div>
        <Button variant="flat" onClick={() => navigate('/persona-studio')}>
          <Icon name="arrow-left" size="md" />
          <span className="ml-2">Back to Library</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left panel - Controls */}
        <div className="space-y-6">
          {/* Persona selector */}
          <Card skin="bordered" className="p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Select Persona</h3>
            <Select
              value={selectedPersonaId}
              onChange={(e) => handlePersonaChange(e.target.value)}
              data={personaOptions}
              disabled={isLoadingPersonas}
            />
            {isLoadingPersonas && (
              <p className="text-sm text-gray-500 dark:text-dark-400 mt-2">Loading personas...</p>
            )}
          </Card>

          {/* Topic input */}
          <Card skin="bordered" className="p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Content Topic</h3>
            <Input
              placeholder="Enter a custom topic..."
              value={customTopic}
              onChange={(e) => setCustomTopic(e.target.value)}
              className="mb-3"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-2">
                Or choose a sample topic:
              </label>
              <div className="flex flex-wrap gap-2">
                {SAMPLE_PROMPTS.map((prompt) => (
                  <Button
                    key={prompt}
                    variant={prompt === selectedTopic && !customTopic ? 'filled' : 'soft'}
                    className="text-xs px-2 py-1"
                    onClick={() => {
                      setSelectedTopic(prompt);
                      setCustomTopic('');
                    }}
                  >
                    {prompt}
                  </Button>
                ))}
              </div>
            </div>
          </Card>

          {/* Channel & Language selectors */}
          <Card skin="bordered" className="p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Channel</h3>
                <Select
                  value={selectedChannel}
                  onChange={(e) => setSelectedChannel(e.target.value)}
                  data={channelOptions}
                />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Language</h3>
                <Select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  data={LANGUAGE_OPTIONS}
                />
              </div>
            </div>
          </Card>

          {/* AI Feedback Chat Box */}
          {workingPersona && (
            <Card
              skin="bordered"
              className="p-4 bg-gradient-to-br from-primary-50 to-info-50 dark:from-primary-900/20 dark:to-info-900/20"
            >
              <div className="flex items-center gap-2 mb-3">
                <Icon name="sparkles" size="md" className="text-primary-500" />
                <h3 className="font-semibold text-gray-900 dark:text-white">AI Persona Coach</h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-dark-300 mb-3">
                Describe issues with the generated output and get suggestions to improve your
                persona.
              </p>
              <Textarea
                placeholder="e.g., 'The output is too formal for my audience' or 'I want more energy and enthusiasm' or 'Make it sound more casual like talking to a friend'"
                value={feedbackInput}
                onChange={(e) => setFeedbackInput(e.target.value)}
                rows={3}
                className="mb-3"
              />
              <Button
                color="primary"
                onClick={handleAnalyzeFeedback}
                disabled={isAnalyzing || !feedbackInput.trim()}
                className="w-full"
              >
                {isAnalyzing ? (
                  <>
                    <Icon name="refresh" size="sm" className="animate-spin" />
                    <span className="ml-2">Analyzing...</span>
                  </>
                ) : (
                  <>
                    <Icon name="sparkles" size="sm" />
                    <span className="ml-2">Get Suggestions</span>
                  </>
                )}
              </Button>

              {/* Suggestions display */}
              {suggestions && (
                <div className="mt-4 p-3 bg-white dark:bg-dark-800 rounded-lg border border-primary-200 dark:border-primary-800">
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                    AI Suggestions:
                  </p>
                  <p className="text-sm text-gray-600 dark:text-dark-300 mb-3">
                    {suggestions.summary}
                  </p>
                  {suggestions.suggestions.length > 0 && (
                    <div className="space-y-2">
                      {suggestions.suggestions.map((suggestion, idx) => (
                        <div
                          key={idx}
                          className="p-2 bg-gray-50 dark:bg-dark-700 rounded flex items-center justify-between"
                        >
                          <div>
                            <span className="text-xs font-medium text-gray-700 dark:text-dark-200">
                              {suggestion.field}:
                            </span>
                            <span className="text-xs text-gray-500 dark:text-dark-400 ml-2">
                              {suggestion.current} → {suggestion.suggested}
                            </span>
                          </div>
                          <Button
                            variant="soft"
                            className="text-xs"
                            onClick={() => handleApplySuggestion(suggestion)}
                          >
                            Apply
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <Button
                    variant="flat"
                    className="mt-3 text-xs"
                    onClick={() => {
                      setSuggestions(null);
                      setFeedbackInput('');
                    }}
                  >
                    Dismiss
                  </Button>
                </div>
              )}
            </Card>
          )}

          {/* Decision adjustments */}
          {workingPersona && (
            <Card skin="bordered" className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 dark:text-white">Quick Adjustments</h3>
                {hasUnsavedChanges && (
                  <span className="text-xs text-warning-600 dark:text-warning-400">
                    Unsaved changes
                  </span>
                )}
              </div>
              <div className="space-y-6">
                {DECISION_DIMENSIONS.filter((d) =>
                  ['confidence', 'formality', 'energy', 'callToAction'].includes(d.key)
                ).map((dimension) => (
                  <DecisionSelector
                    key={dimension.key}
                    dimension={dimension}
                    value={
                      workingPersona.decisions?.[dimension.key] ?? defaultDecisions[dimension.key]
                    }
                    onChange={updateDecision}
                    compact
                  />
                ))}
              </div>

              {/* Save/Reset buttons */}
              <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-dark-600">
                <Button
                  color="primary"
                  onClick={handleSave}
                  disabled={!hasUnsavedChanges || isUpdating}
                  className="flex-1"
                >
                  {isUpdating ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button variant="flat" onClick={handleReset} disabled={!hasUnsavedChanges}>
                  Reset
                </Button>
              </div>
            </Card>
          )}
        </div>

        {/* Right panel - Preview */}
        <div className="space-y-6">
          {!selectedPersonaId ? (
            <Card
              skin="bordered"
              className="p-12 text-center border-2 border-dashed border-gray-300 dark:border-dark-500"
            >
              <div className="max-w-md mx-auto">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                  <Icon name="zap" size="xl" className="text-primary-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Select a persona to begin
                </h3>
                <p className="text-gray-500 dark:text-dark-400">
                  Choose a persona from the dropdown to see live content generation and make
                  adjustments.
                </p>
              </div>
            </Card>
          ) : workingPersona ? (
            <>
              {/* Language indicator */}
              {selectedLanguage === 'hebrew' && (
                <Card
                  skin="bordered"
                  className="p-3 bg-info-50 dark:bg-info-900/20 border-info-200 dark:border-info-800"
                >
                  <div className="flex items-center gap-2">
                    <Icon name="info" size="sm" className="text-info-500" />
                    <p className="text-sm text-info-700 dark:text-info-300">
                      Output language: Hebrew (עברית) - Preview shows English template, actual
                      generation will be in Hebrew.
                    </p>
                  </div>
                </Card>
              )}

              {/* Live preview */}
              <PersonaPreview
                persona={workingPersona}
                selectedChannel={selectedChannel}
                topic={effectiveTopic}
                language={selectedLanguage}
                onChannelChange={setSelectedChannel}
                useRealGeneration={true}
              />

              {/* Quick actions */}
              <Card skin="bordered" className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-dark-300">
                    Want to make more changes?
                  </span>
                  <Button
                    variant="soft"
                    onClick={() => navigate(`/persona-studio/${selectedPersonaId}/edit`)}
                  >
                    <Icon name="edit" size="sm" />
                    <span className="ml-1">Edit Full Persona</span>
                  </Button>
                </div>
              </Card>
            </>
          ) : (
            <Card skin="bordered" className="p-12 text-center">
              <div className="animate-pulse">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-200 dark:bg-dark-600" />
                <div className="h-4 bg-gray-200 dark:bg-dark-600 rounded w-32 mx-auto" />
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
